import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import type { Employee, EmployeeMonthlyInfo } from '../../types/employee';
import { resolvePayType, isDailyWorkerPay, isBusinessServicePay } from '../../types/employee';
import { netPayFromMonthlyInfo } from '../../lib/payroll/net-pay';
import { deductionLabel } from '../../lib/payroll/display-helper';
import { estimateDayInsurance } from '../../lib/payroll/daily-worker-insurance';
import { dayInsuranceRatesFromRegulatory } from '../../lib/payroll/types';
import { useCurrentRates } from '../../hooks/useRates';
import { formatNumber } from '../../utils/formatters';
import EmployeePayPreviewCard from './EmployeePayPreviewCard';
import DailyWorkerInsuranceHint from './DailyWorkerInsuranceHint';
import PayTypeBadge from './PayTypeBadge';

interface EmployeePayDrawerProps {
  open: boolean;
  onClose: () => void;
  employee: Employee | null;
  monthlyInfo: EmployeeMonthlyInfo | null;
  onEdit?: (employee: Employee) => void;
}

const EmployeePayDrawer: React.FC<EmployeePayDrawerProps> = ({
  open,
  onClose,
  employee,
  monthlyInfo,
  onEdit,
}) => {
  const rates = useCurrentRates();

  if (!employee || !monthlyInfo) return null;

  const payType = resolvePayType(employee.payType, employee.isHourly);
  const workDays = monthlyInfo.workDays ?? 0;
  const net = netPayFromMonthlyInfo(monthlyInfo);
  const label = deductionLabel(employee, monthlyInfo, workDays);
  const estimate = isDailyWorkerPay(employee)
    ? estimateDayInsurance(employee, dayInsuranceRatesFromRegulatory(rates))
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          p: 0,
          bgcolor: 'var(--bg-base)',
        },
      }}
    >
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--fg-strong)' }}>
            {employee.name}
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            <PayTypeBadge payType={payType} />
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label="닫기"
          sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
          {GLYPHS.close}
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 2.5 }}>
        {(isDailyWorkerPay(employee) || isBusinessServicePay(employee)) && workDays > 0 && (
          <Typography variant="body2" sx={{ color: 'var(--fg-secondary)', mb: 2 }}>
            근무 {workDays}일 · 실수령{' '}
            <Typography component="span" className="ds-num-md" sx={{ color: 'var(--primary)', fontWeight: 700 }}>
              {formatNumber(net)}원
            </Typography>
          </Typography>
        )}

        <EmployeePayPreviewCard
          payType={payType}
          grossAmount={
            isDailyWorkerPay(employee) || isBusinessServicePay(employee)
              ? (employee.dailyRate ?? 0) * Math.max(1, workDays)
              : monthlyInfo.totalSalary
          }
          grossLabel={
            workDays > 1
              ? isDailyWorkerPay(employee)
                ? '일당 합계'
                : '지급 합계'
              : undefined
          }
          employee={employee}
          insuranceWorkDays={Math.max(1, workDays)}
          showInsuranceHint={false}
        />

        {isDailyWorkerPay(employee) && estimate && (
          <DailyWorkerInsuranceHint estimate={estimate} workDays={workDays} />
        )}

        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'var(--bg-muted)' }}>
          <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)' }}>
            공제: {label} · {formatNumber(monthlyInfo.totalEmployeeInsurance)}원
          </Typography>
        </Box>

        {onEdit && (
          <Typography
            component="button"
            onClick={() => onEdit(employee)}
            sx={{
              mt: 2,
              border: 'none',
              background: 'none',
              color: 'var(--primary)',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.9375rem',
            }}
          >
            직원 정보 수정 →
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default EmployeePayDrawer;
